/**
 * Project template generators
 */

import type { ProjectConfig, GeneratedFile, TemplateMetadata } from './types.js';

/**
 * Template metadata
 */
export const TEMPLATES: Record<string, TemplateMetadata> = {
  minimal: {
    name: 'Minimal',
    description: 'Basic agent setup with tools and FinOps',
    features: ['Single agent file', 'Basic tools', 'FinOps tracking'],
  },
  dashboard: {
    name: 'Dashboard',
    description: 'Agent with web dashboard and real-time monitoring',
    features: ['Express server', 'Real-time metrics', 'Web UI'],
  },
  serverless: {
    name: 'Serverless',
    description: 'Edge-ready serverless function with agent',
    features: ['Vercel/Cloudflare ready', 'Streaming responses', 'Budget controls'],
  },
};

/**
 * Generate package.json
 */
export function generatePackageJson(config: ProjectConfig): string {
  const dependencies: Record<string, string> = {
    '@thenoo/agentkit': '^0.2.0',
    '@thenoo/finops': '^0.1.1',
    'zod': '^3.23.0',
  };

  if (config.adapter === 'openai') {
    dependencies['@thenoo/adapter-openai'] = '^0.2.0';
  } else if (config.adapter === 'azure') {
    dependencies['@thenoo/adapter-azure'] = '^0.2.0';
  }

  if (config.template === 'dashboard') {
    dependencies['express'] = '^4.18.2';
    dependencies['cors'] = '^2.8.5';
  }

  const devDependencies: Record<string, string> = {};

  if (config.typescript) {
    devDependencies['typescript'] = '^5.4.5';
    devDependencies['@types/node'] = '^20.11.19';
    devDependencies['tsx'] = '^4.7.0';
    
    if (config.template === 'dashboard') {
      devDependencies['@types/express'] = '^4.17.21';
      devDependencies['@types/cors'] = '^2.8.17';
    }
  }

  const scripts: Record<string, string> = {
    start: config.typescript ? 'tsx src/index.ts' : 'node src/index.js',
    dev: config.typescript ? 'tsx watch src/index.ts' : 'node --watch src/index.js',
  };

  if (config.typescript) {
    scripts.build = 'tsc';
    scripts.typecheck = 'tsc --noEmit';
  }

  if (config.template === 'dashboard') {
    scripts['start:server'] = config.typescript ? 'tsx src/server.ts' : 'node src/server.js';
  }

  const template = TEMPLATES[config.template];
  const description = template ? `${template.name} agent project` : 'AI agent project';

  return JSON.stringify(
    {
      name: config.name,
      version: '0.1.0',
      description,
      type: 'module',
      scripts,
      dependencies,
      devDependencies,
      engines: {
        node: '>=18',
      },
    },
    null,
    2
  );
}

/**
 * Generate tsconfig.json
 */
export function generateTsConfig(config: ProjectConfig): string | null {
  if (!config.typescript) {
    return null;
  }

  return JSON.stringify(
    {
      compilerOptions: {
        target: 'ES2022',
        module: 'ESNext',
        lib: ['ES2022'],
        moduleResolution: 'Bundler',
        esModuleInterop: true,
        skipLibCheck: true,
        strict: true,
        resolveJsonModule: true,
        outDir: './dist',
        rootDir: './src',
        declaration: true,
        declarationMap: true,
        sourceMap: true,
      },
      include: ['src/**/*.ts'],
      exclude: ['node_modules', 'dist'],
    },
    null,
    2
  );
}

/**
 * Generate main agent file
 */
export function generateMainFile(config: ProjectConfig): string {
  const typeImports = config.typescript ? 'import type { AgentResult } from \'@thenoo/agentkit\';\n' : '';

  let imports = `import { createAgent, tool } from '@thenoo/agentkit';\nimport { createBudgetManager } from '@thenoo/finops';\nimport { z } from 'zod';\n${typeImports}`;

  let adapterSetup = '';
  let adapterUsage = '';

  if (config.adapter === 'openai') {
    imports += `import { createOpenAIAdapter, withFinOps } from '@thenoo/adapter-openai';\n`;
    adapterSetup = `
// Initialize OpenAI adapter with FinOps tracking
const adapter = createOpenAIAdapter({
  apiKey: process.env.OPENAI_API_KEY || '',
  defaultModel: 'gpt-4o-mini',
});

const wrappedAdapter = withFinOps(adapter, {
  sessionId: 'demo-session',
  budget: {
    usd: 5,
    perRequestCents: 25,
    policy: 'alert',
  },
  onAlert: (event) => {
    console.warn('⚠️ Budget alert:', event);
  },
});
`;
    adapterUsage = `
  // Use adapter for real LLM calls (v0.3.0+)
  // adapter: wrappedAdapter,
`;
  } else if (config.adapter === 'azure') {
    imports += `import { createAzureAdapter, withFinOps } from '@thenoo/adapter-azure';\n`;
    adapterSetup = `
// Initialize Azure OpenAI adapter with FinOps tracking
const adapter = createAzureAdapter({
  apiKey: process.env.AZURE_OPENAI_KEY || '',
  endpoint: process.env.AZURE_OPENAI_ENDPOINT || '',
  deployment: 'gpt-4o-mini',
});

const wrappedAdapter = withFinOps(adapter, {
  sessionId: 'demo-session',
  budget: {
    usd: 5,
    perRequestCents: 25,
    policy: 'alert',
  },
  onAlert: (event) => {
    console.warn('⚠️ Budget alert:', event);
  },
});
`;
    adapterUsage = `
  // Use adapter for real LLM calls (v0.3.0+)
  // adapter: wrappedAdapter,
`;
  }

  return `${imports}
${adapterSetup}
// Define tools
const calculator = tool({
  name: 'calculator',
  description: 'Perform basic arithmetic operations',
  input: z.object({
    operation: z.enum(['add', 'subtract', 'multiply', 'divide']),
    a: z.number(),
    b: z.number(),
  }),
  output: z.object({
    result: z.number(),
  }),
  run: async ({ operation, a, b }) => {
    let result${config.typescript ? ': number' : ''};
    switch (operation) {
      case 'add':
        result = a + b;
        break;
      case 'subtract':
        result = a - b;
        break;
      case 'multiply':
        result = a * b;
        break;
      case 'divide':
        if (b === 0) throw new Error('Division by zero');
        result = a / b;
        break;
    }
    return { result };
  },
});

// Create budget manager
const budget = createBudgetManager({
  usd: 10,
  perRequestCents: 50,
  onExceed: 'halt',
});

// Create agent
const agent = createAgent({
  name: '${config.name}',
  tools: [calculator],
  budget,${adapterUsage}
});

// Listen to agent events
agent.on('tool:call', (event) => {
  console.log(\`🔧 Calling tool: \${event.name}\`);
});

agent.on('cost:update', (event) => {
  console.log(\`💰 Cost update: $\${event.payload.totalUsd.toFixed(4)}\`);
});

// Main function
async function main() {
  console.log('🚀 Starting ${config.name}...\\n');

  try {
    const result${config.typescript ? ': AgentResult' : ''} = await agent.run(
      'Calculate 42 * 137'
    );

    console.log('\\n✅ Result:', result.output);
    console.log('📊 Budget:', budget.getState());
  } catch (error) {
    console.error('❌ Error:', error${config.typescript ? ' as Error' : ''});
    process.exit(1);
  }
}

main();
`;
}

/**
 * Generate README.md
 */
export function generateReadme(config: ProjectConfig): string {
  const installCmd = `npm install`;
  const startCmd = `npm start`;

  let envVars = '';
  if (config.adapter === 'openai') {
    envVars = `
## Environment Variables

Create a \`.env\` file:

\`\`\`bash
OPENAI_API_KEY=your_openai_api_key
\`\`\`
`;
  } else if (config.adapter === 'azure') {
    envVars = `
## Environment Variables

Create a \`.env\` file:

\`\`\`bash
AZURE_OPENAI_KEY=your_azure_key
AZURE_OPENAI_ENDPOINT=https://your-resource.openai.azure.com
\`\`\`
`;
  }

  const template = TEMPLATES[config.template];
  const templateDesc = template?.description || 'AI agent project';
  const templateFeatures = template?.features || ['Agent creation', 'Tool support', 'FinOps tracking'];

  return `# ${config.name}

> ${templateDesc}

Built with [@thenoo/agentkit](https://github.com/ZacheryKuykendall/noo/tree/main/packages/agentkit).

## Features

${templateFeatures.map((f) => `- ${f}`).join('\n')}

## Getting Started

### Installation

\`\`\`bash
${installCmd}
\`\`\`
${envVars}
### Run

\`\`\`bash
${startCmd}
\`\`\`

## Project Structure

\`\`\`
${config.name}/
├── src/
│   └── index.${config.typescript ? 'ts' : 'js'}  # Main agent file
├── package.json
${config.typescript ? '├── tsconfig.json\n' : ''}└── README.md
\`\`\`

## Learn More

- [@thenoo/agentkit Documentation](https://github.com/ZacheryKuykendall/noo/tree/main/packages/agentkit)
- [@thenoo/finops Documentation](https://github.com/ZacheryKuykendall/noo/tree/main/packages/finops)
${config.adapter === 'openai' ? '- [@thenoo/adapter-openai Documentation](https://github.com/ZacheryKuykendall/noo/tree/main/packages/adapter-openai)\n' : ''}${config.adapter === 'azure' ? '- [@thenoo/adapter-azure Documentation](https://github.com/ZacheryKuykendall/noo/tree/main/packages/adapter-azure)\n' : ''}
## License

MIT
`;
}

/**
 * Generate .gitignore
 */
export function generateGitignore(): string {
  return `# Dependencies
node_modules/

# Build output
dist/
build/
*.tsbuildinfo

# Environment
.env
.env.local

# IDE
.vscode/
.idea/
*.swp
*.swo
*~

# OS
.DS_Store
Thumbs.db

# Logs
*.log
npm-debug.log*
`;
}

/**
 * Generate GitHub CI workflow
 */
export function generateGitHubCI(config: ProjectConfig): string {
  const testStep = config.typescript
    ? `
      - name: Type check
        run: npm run typecheck`
    : '';

  return `name: CI

on:
  push:
    branches: [main]
  pull_request:
    branches: [main]

jobs:
  test:
    runs-on: ubuntu-latest

    steps:
      - uses: actions/checkout@v4

      - name: Setup Node.js
        uses: actions/setup-node@v4
        with:
          node-version: '18'
          cache: 'npm'

      - name: Install dependencies
        run: npm ci
${testStep}

      - name: Run agent
        run: npm start
        env:
          OPENAI_API_KEY: \${{ secrets.OPENAI_API_KEY }}
`;
}

/**
 * Generate all project files
 */
export function generateProjectFiles(config: ProjectConfig): GeneratedFile[] {
  const files: GeneratedFile[] = [
    {
      path: 'package.json',
      content: generatePackageJson(config),
    },
    {
      path: `src/index.${config.typescript ? 'ts' : 'js'}`,
      content: generateMainFile(config),
    },
    {
      path: 'README.md',
      content: generateReadme(config),
    },
    {
      path: '.gitignore',
      content: generateGitignore(),
    },
    {
      path: '.github/workflows/ci.yml',
      content: generateGitHubCI(config),
    },
  ];

  if (config.typescript) {
    const tsconfig = generateTsConfig(config);
    if (tsconfig !== null) {
      files.push({
        path: 'tsconfig.json',
        content: tsconfig,
      });
    }
  }

  return files;
}

